<template>
  <div ref="gaugeEl" class="compliance-gauge"></div>
</template>
<script>
import * as echarts from "echarts";

/** Indigenous compliance — brand pink below target (Vuetify primary); teal when at/above. */
const BELOW_TARGET = "#ff1463";
const MEETS_TARGET = "#4EB888";

export default {
  props: {
    percent: { type: Number, default: 0 },
    target: { type: Number, default: 4 },
    fyLabel: { type: String, default: "" },
    changed: { type: Number, default: 0 }
  },
  data() {
    return {
      chart: null,
      resizeObserver: null
    };
  },
  watch: {
    percent() {
      this.scheduleRender();
    },
    target() {
      this.scheduleRender();
    },
    fyLabel() {
      this.scheduleRender();
    },
    changed() {
      this.scheduleRender();
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.setupResizeObserver();
      this.renderGauge();
    });
  },
  beforeDestroy() {
    this.teardownResizeObserver();
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  },
  methods: {
    scheduleRender() {
      this.$nextTick(() => {
        this.renderGauge();
      });
    },
    setupResizeObserver() {
      const el = this.$refs.gaugeEl;
      if (!el || typeof ResizeObserver === "undefined") {
        return;
      }
      this.resizeObserver = new ResizeObserver(() => {
        if (this.chart) {
          this.chart.resize();
        }
      });
      this.resizeObserver.observe(el);
    },
    teardownResizeObserver() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    },
    safeNum(n, fallback = 0) {
      const x = Number(n);
      return Number.isFinite(x) ? x : fallback;
    },
    renderGauge() {
      const el = this.$refs.gaugeEl;
      if (!el) return;

      const pct = this.safeNum(this.percent, 0);
      const tgt = this.safeNum(this.target, 4);
      const maxVal = Math.max(
        Math.ceil((Math.max(pct, tgt) * 2) / 2) * 2,
        tgt * 2,
        8
      );
      const tgtRatio = tgt / maxVal;
      const isCompliant = pct >= tgt;
      const diff = Math.abs(pct - tgt).toFixed(1);
      const fy = this.fyLabel || "";

      if (this.chart) {
        this.chart.dispose();
        this.chart = null;
      }
      this.chart = echarts.init(el);

      // Do not use markLine on gauge series — it is for cartesian charts and can prevent rendering.
      const option = {
        series: [
          {
            type: "gauge",
            startAngle: 180,
            endAngle: 0,
            min: 0,
            max: maxVal,
            radius: "100%",
            center: ["50%", "70%"],
            axisLine: {
              lineStyle: {
                width: 20,
                color: [
                  [tgtRatio, BELOW_TARGET],
                  [1, MEETS_TARGET]
                ]
              }
            },
            axisTick: { show: false },
            splitLine: { show: false },
            axisLabel: {
              distance: -30,
              fontSize: 11,
              color: "#666",
              formatter: (val) => {
                if (val === 0 || val === maxVal || val === tgt) {
                  return val + "%";
                }
                return "";
              }
            },
            pointer: {
              length: "60%",
              width: 5,
              itemStyle: {
                color: isCompliant ? MEETS_TARGET : BELOW_TARGET
              }
            },
            detail: {
              valueAnimation: true,
              formatter: () => {
                return (
                  "{val|" + pct.toFixed(1) + "% of spend}" +
                  "\n{fy|" + fy + "}" +
                  "\n{status|" +
                  (isCompliant
                    ? "Meets target (" + diff + " pp above " + tgt + "%)"
                    : "Below target (" + diff + " pp under " + tgt + "%)") +
                  "}"
                );
              },
              rich: {
                val: {
                  fontSize: 26,
                  fontWeight: "bold",
                  color: isCompliant ? MEETS_TARGET : BELOW_TARGET,
                  lineHeight: 32
                },
                fy: {
                  fontSize: 13,
                  color: "#666",
                  lineHeight: 22
                },
                status: {
                  fontSize: 12,
                  color: isCompliant ? MEETS_TARGET : BELOW_TARGET,
                  lineHeight: 22,
                  fontWeight: "500"
                }
              },
              offsetCenter: [0, "20%"]
            },
            data: [{ value: pct }],
            title: { show: false }
          }
        ]
      };

      this.chart.setOption(option);

      this.$nextTick(() => {
        if (this.chart) {
          this.chart.resize();
        }
      });
    }
  }
};
</script>
<style scoped>
.compliance-gauge {
  width: 100%;
  min-width: 200px;
  height: 300px;
}
</style>
